<template>
  <div class="fts-login">
    <div class="dummy-element"></div>
    <div class="login-content" v-if="loaded && !isError">
      <div class="login-content-wrapper">
        <div class="login-intro">
          <div class="login-intro-header"></div>
          <img class="login-intro-content" src="../../assets/images/client/loginIntro2.svg" />
        </div>
        <!-- form -->
        <div class="login-form">
          <!-- join flow -->
          <div v-if="state == 'join'" :key="state">
            <!-- header -->
            <div class="login-form-title">
              <template v-if="formData.existing">
                <div class="login-form-title-main">Join a workspace</div>
                <div class="login-form-title-sub">One more step to start collaborating!</div>
              </template>
              <template v-else>
                <div class="login-form-title-main">Join a new workspace</div>
                <div class="login-form-title-sub">One more step to start creating!</div>
              </template>              
            </div>
            <!-- content -->
            <form @submit.prevent="activateWorkspace" autocomplete="off" novalidate>
              <!-- workspace -->
              <div class="workspace-section">
                <div class="workspace-label">
                  <template v-if="formData.existing">
                    You are joining an existing workspace
                  </template>
                  <template v-else>
                    You are creating a new workspace for your team or organization.
                  </template>
                </div>
                <div
                  class="workspace-name" v-if="formData.existing">
                  {{ formData.workspace }}
                </div>
              </div>
              <!-- username (email) -->
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{ 'with-error': error({ fieldName: 'username' }) }"
              >
                <font-awesome-icon
                  :icon="['far', 'envelope']"
                  fixed-width
                  class="fts-form-icon"
                />
                <input
                  type="text"
                  class="fts-form-control disabled"
                  :value="formData.username"
                  disabled
                  v-if="fixedUsername"
                />
                <input
                  type="text"
                  class="fts-form-control"
                  placeholder="Email"
                  autocomplete="join-username"
                  v-model="formData.username"
                  @input="onInput('username')"
                  @blur="onBlur('username')"
                  v-else
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('username')"
                />
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'username' })"
                >
                  {{ error({ fieldName: "username", displayName: "Email" }) }}
                </div>
              </div>
              <template v-if="formData.username">
                <!-- password -->
                <div
                  v-if="!formData.provider"
                  class="fts-form-group fts-input-group with-check"
                  :class="{
                    'with-error': error({
                      fieldName: 'password',
                      displayName: 'Password',
                    }),
                  }"
                >
                  <font-awesome-icon
                    :icon="['fas', 'lock']"
                    class="fts-form-icon"
                  />
                  <input
                    type="password"
                    class="fts-form-control"
                    placeholder="Password"
                    autocomplete="new-password"
                    v-model="formData.password"
                    @paste.prevent
                    @input="onInput('password')"
                    @blur="onBlur('password')"
                  />
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    fixed-width
                    class="fts-form-icon-check"
                    v-if="checkIndicator('password')"
                  />
                  <div
                    class="fts-form-helper"
                    v-if="
                      !(
                        $v.formData &&
                        $v.formData.password &&
                        $v.formData.password.$error &&
                        $v.formData.password.required
                      )
                    "
                  >
                    Password must contain 1 alphabet, 1 numeric, and at least 8
                    characters.
                  </div>
                  <div
                    class="fts-form-error"
                    v-if="error({ fieldName: 'password', displayName: 'Password' })"
                  >
                    {{ error({ fieldName: "password", displayName: "Password" }) }}
                  </div>
                </div>

                <!-- confirm password -->
                <div
                  v-if="!formData.provider"
                  class="fts-form-group fts-input-group with-check"
                  :class="{
                    'with-error': error({
                      fieldName: 'confirmPassword',
                      displayName: 'Confirm Password',
                    }),
                  }"
                >
                  <font-awesome-icon
                    :icon="['fas', 'lock']"
                    class="fts-form-icon"
                  />
                  <input
                    type="password"
                    class="fts-form-control"
                    placeholder="Confirm your password again"
                    autocomplete="new-password"
                    v-model="formData.confirmPassword"
                    @paste.prevent
                    @input="onInput('confirmPassword')"
                    @blur="onBlur('confirmPassword')"
                  />
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    fixed-width
                    class="fts-form-icon-check"
                    v-if="checkIndicator('confirmPassword')"
                  />
                  <div
                    class="fts-form-error"
                    v-if="
                      error({
                        fieldName: 'confirmPassword',
                        displayName: 'Confirm Password',
                      })
                    "
                  >
                    {{
                      error({
                        fieldName: "confirmPassword",
                        displayName: "Confirm Password",
                      })
                    }}
                  </div>
                </div>
                <!-- workspace name -->
                <div
                  class="fts-form-group fts-input-group with-check"
                  :class="{ 'with-error': error({ fieldName: 'workspace' }) }"
                  v-if="!formData.existing"
                >
                  <font-awesome-icon
                    :icon="['fas', 'laptop']"
                    fixed-width
                    class="fts-form-icon"
                  />
                  <input
                    type="text"
                    class="fts-form-control"
                    placeholder="Workspace name, eg. Acme Corporation"
                    autocomplete="join-workspace"
                    v-model="formData.workspace"
                    @input="onInput('workspace')"
                    @blur="onBlur('workspace')"
                  />
                  <!-- workspace name hint -->
                  <div
                    class="workspace-name-hint"
                    v-if="
                      $v.formData &&
                      $v.formData.workspace &&
                      $v.formData.workspace.$error &&
                      $v.formData.workspace.format === false
                    "
                  >
                    <div class="workspace-name-hint-wrapper">Allow alphabet and numeric only.</div>
                    <div class="workspace-name-hint-wrapper" v-if="workspaceNameSpecialCharacters">
                      Allow special characters
                      <span
                        class="workspace-name-hint-highlight"
                        v-for="(x, index) in workspaceNameSpecialCharacters"
                        :key="`workspace-special-character-${index}`"
                      >
                        {{x}}
                      </span>
                    </div>
                  </div>
                  <!-- validation -->
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    fixed-width
                    class="fts-form-icon-check"
                    v-if="checkIndicator('workspace')"
                  />
                  <div class="login-form-error-msg">{{ workspaceError }}</div>
                  <div
                    class="fts-form-error"
                    v-if="error({ fieldName: 'workspace' }) && !workspaceError"
                  >
                    {{
                      error({
                        fieldName: "workspace",
                        displayName: "Workspace name",
                      })
                    }}
                  </div>
                </div>
                <!-- display name -->
                <div
                  class="fts-form-group fts-input-group with-check"
                  :class="{ 'with-error': error({ fieldName: 'displayName' }) }"
                >
                  <font-awesome-icon
                    :icon="['far', 'user']"
                    fixed-width
                    class="fts-form-icon"
                  />
                  <input
                    type="text"
                    class="fts-form-control"
                    placeholder="Your display name, eg. John Smith"
                    autocomplete="join-display-name"
                    v-model="formData.displayName"
                    @input="onInput('displayName')"
                    @blur="onBlur('displayName')"
                  />
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    fixed-width
                    class="fts-form-icon-check"
                    v-if="checkIndicator('displayName')"
                  />
                  <div
                    class="fts-form-error"
                    v-if="error({ fieldName: 'displayName' })"
                  >
                    {{
                      error({
                        fieldName: "displayName",
                        displayName: "Display Name",
                      })
                    }}
                  </div>
                </div>
              </template>
              <!-- action -->
              <fts-btn
                type="submit"
                class="login-form-action"
                color="primary"
                text-transform-init
                box-shadow-none
              >
                <template v-if="formData.existing">Join</template>
                <template v-else>Activate</template>
              </fts-btn>
            </form>
            <!-- third party -->
            <template v-if="thirdPartyLogin && !formData.username">
              <!-- divider -->
              <div class="divider">
                <div class="divider-line" />
                <div class="divider-label">or</div>
                <div class="divider-line" />
              </div>
              <!-- google register -->
              <fts-btn
                class="login-form-action google-sign-in"
                color="#ffffff"
                text-transform-init
                box-shadow-none
                @click="googleLogin"
              >
                <!-- @click="" -->
                <img style="margin-right: 16px;" width="20" src="../../assets/images/brand/google.png" />
                Continue with Google
              </fts-btn>
              <!-- apple register -->
              <vue-apple-login
                color="black"
                :border="true"
                type="continue"
                width="212"
                height="46"
                class="cust-apple-login"
                :onSuccess="onSuccess"
                :onFailure="onFailure"
              />
            </template>
            <!-- term and policy -->
            <div class="login-form-term-policy">
              By joining up, you agree to Inistate's 
              <a href="https://inistate.com/terms" target="_blank">terms of services </a>
              and 
              <a href="https://inistate.com/privacyPolicy" target="_blank">privacy policy</a>.
            </div>
          </div>
          <!-- activate -->
          <div v-else-if="state == 'activate'" :key="state">
            <!-- header -->
            <div class="login-form-title">
              <div class="login-form-title-main">Activation</div>
              <div class="login-form-title-sub">Please wait a moment.</div>
            </div>
            <!-- content -->
            <div></div>
          </div>
          <!-- forgot password -->
          <div v-else-if="state == 'forgotPassword'">
            <!-- header -->
            <div class="login-form-title">
              <div class="login-form-title-main">Forgot/reset password</div>
              <div class="login-form-title-sub">
                If your account does not have an email address, please reach out to our support team on WhatsApp.
              </div>
            </div>
            <!-- content -->
            <form @submit.prevent="requestReset" autocomplete="off" novalidate>
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{'with-error': error({fieldName: 'username'})}"
              >
                <font-awesome-icon
                  :icon="['far', 'envelope']"
                  fixed-width
                  class="fts-form-icon"
                />
                <input
                  type="text"
                  class="fts-form-control"
                  placeholder="Email"
                  autocomplete="forgot-password-username"
                  v-model="formData.username"
                  @input="onInput('username')"
                  @blur="onBlur('username')"
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('username')"
                />
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'username' })"
                >
                  {{ error({ fieldName: "username", displayName: "Email" }) }}
                </div>
              </div>
              <!-- action -->
              <fts-btn
                type="submit"
                class="login-form-action"
                color="var(--pcd-green)"
                text-transform-init
                box-shadow-none
              >
                Get reset link
              </fts-btn>
              <!-- divider -->
              <div class="divider">
                <div class="divider-line" />
                <div class="divider-label">or</div>
                <div class="divider-line" />
              </div>
              <!-- login -->
              <router-link :to="{ name: 'login' }" custom v-slot="{ navigate }">
                <fts-btn
                  text
                  class="login-form-action"
                  color="var(--pcd-green)"
                  text-transform-init
                  box-shadow-none
                  @click.native="navigate"
                >
                  Back to sign in
                </fts-btn>
              </router-link>
            </form>
          </div>      
          <!-- reset -->
          <div v-else-if="state == 'reset'">
            <!-- header -->
            <div class="login-form-title">
              <div class="login-form-title-main">Set new password</div>
              <div class="login-form-title-sub" v-if="formData.username">for {{formData.username}}.</div>
              <div class="login-form-title-sub" v-else>One more step to recover your account !</div>
            </div>
            <!-- content -->
            <form @submit.prevent="resetPassword" autocomplete="off" novalidate>
              <!-- password -->
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{ 'with-error': error({ fieldName: 'password' }) }"
              >
                <font-awesome-icon
                  :icon="['fas', 'lock']"
                  class="fts-form-icon"
                />
                <input
                  type="password"
                  class="fts-form-control"
                  placeholder="Password"
                  autocomplete="new-password"
                  v-model="formData.password"
                  @paste.prevent
                  @input="onInput('password')"
                  @blur="onBlur('password')"
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('password')"
                />
                <div
                  class="fts-form-helper"
                  v-if="
                    !(
                      $v.formData &&
                      $v.formData.password &&
                      $v.formData.password.$error &&
                      $v.formData.password.required
                    )
                  "
                >
                  Password must contain 1 alphabet, 1 numeric, and at least 8
                  characters.
                </div>
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'password' })"
                >
                  {{ error({ fieldName: "password", displayName: "Password" }) }}
                </div>
              </div>
              <!-- confirm password -->
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{ 'with-error': error({ fieldName: 'confirmPassword' }) }"
              >
                <font-awesome-icon
                  :icon="['fas', 'lock']"
                  class="fts-form-icon"
                />
                <input
                  type="password"
                  class="fts-form-control"
                  placeholder="Confirm your password again"
                  autocomplete="new-password"
                  v-model="formData.confirmPassword"
                  @paste.prevent
                  @input="onInput('confirmPassword')"
                  @blur="onBlur('confirmPassword')"
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('confirmPassword')"
                />
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'confirmPassword' })"
                >
                  {{
                    error({
                      fieldName: "confirmPassword",
                      displayName: "Confirm Password",
                    })
                  }}
                </div>
              </div>
              <!-- action -->
              <fts-btn
                type="submit"
                class="login-form-action"
                color="var(--pcd-green)"
                text-transform-init
                box-shadow-none
              >
                Reset
              </fts-btn>
            </form>
          </div>
          <!-- create account -->
          <div v-else-if="state == 'createAccount'" :key="state">
            <!-- header -->
            <div class="login-form-title">
              <div class="login-form-title-main">Sign up</div>
              <div class="login-form-title-sub">Let's get started. Just a few short steps!</div>
            </div>
            <!-- content -->
            <!-- checkbox -->
            <div class="fts-form-group login-exist-checkbox">
              <checkbox
                id="joinExistingWorkspace"
                v-model="joinExistingWorkspace"
                color="var(--fts-primary)"
                :size="18"
              />
              <label for="joinExistingWorkspace">
                I wish to join an existing workspace.
              </label>
            </div>
            <!-- login exist helper -->
            <div class="login-exist-helper" v-if="joinExistingWorkspace">
              You need to have an invitation link to join an existing workspace. 
              You may ask the workspace admin or user admin to send you an invitation link.
            </div>
            <!-- form -->
            <template v-else>
              <form @submit.prevent="createAccount" autocomplete="off" novalidate>
                <!-- email (username) -->
                <div
                  class="fts-form-group fts-input-group with-check"
                  :class="[
                    {'with-error': error({fieldName: 'email'})},
                    {'login-sign-up-email': !formData.email}
                  ]"
                >
                  <font-awesome-icon
                    :icon="['far', 'envelope']"
                    fixed-width
                    class="fts-form-icon"
                  />
                  <input
                    type="text"
                    class="fts-form-control"
                    placeholder="Work Email"
                    autocomplete="create-account-email"
                    v-model="formData.email"
                    @input="onInput('email')"
                    @blur="onBlur('email')"
                  />
                  <font-awesome-icon
                    :icon="['far', 'check-circle']"
                    fixed-width
                    class="fts-form-icon-check"
                    v-if="checkIndicator('email')"
                  />
                  <div
                    class="fts-form-error"
                    v-if="error({ fieldName: 'email' })"
                  >
                    {{ error({ fieldName: "email", displayName: "Work email" }) }}
                  </div>
                </div>
                <!-- action -->
                <fts-btn
                  type="submit"
                  class="login-form-action"
                  color="var(--pcd-green)"
                  text-transform-init
                  box-shadow-none
                >
                  Sign up
                </fts-btn>
              </form>
              <template v-if="thirdPartyLogin && !formData.email">
                <!-- third party register -->
                <div class="divider">
                  <div class="divider-line" />
                  <div class="divider-label">or</div>
                  <div class="divider-line" />
                </div>
                <!-- google register -->
                <fts-btn
                  class="login-form-action google-sign-in"
                  color="#ffffff"
                  text-transform-init
                  box-shadow-none
                  @click="googleRegister"
                >
                  <img style="margin-right: 16px;" width="20" src="../../assets/images/brand/google.png" />
                  Sign in with Google
                </fts-btn>
                <!-- apple register -->
                <vue-apple-login
                  color="black"
                  :border="true"
                  type="continue"
                  width="212"
                  height="46"
                  :onSuccess="appleRegisterSuccess"
                  :onFailure="onFailure"
                  class="cust-apple-login"
                />
              </template>
              <!-- term and policy -->
              <div class="login-form-term-policy">
                By signing up, you agree to Inistate's 
                <a href="https://inistate.com/terms" target="_blank">terms of services </a>
                and 
                <a href="https://inistate.com/privacyPolicy" target="_blank">privacy policy</a>.
              </div>
            </template>
            <!-- login -->
            <div class="login-form-create">
              Already have an account?
              <router-link :to="{ name: 'login' }">
                Sign in
              </router-link>
            </div>
          </div>
          <!-- sign in -->
          <div v-else :key="state">
            <!-- header -->
            <div class="login-form-title">
              <div class="login-form-title-main">Sign in</div>
              <div class="login-form-title-sub">To continue, sign in to {{this.companyName}}.</div>
            </div>
            <!-- content -->
            <form @submit.prevent="submit" autocomplete="off" novalidate>
              <!-- username -->
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{ 'with-error': error({ fieldName: 'username' }) }"
              >
                <font-awesome-icon
                  :icon="['far', 'envelope']"
                  fixed-width
                  class="fts-form-icon"
                />
                <input
                  type="text"
                  class="fts-form-control"
                  placeholder="Email"
                  autocomplete="username"
                  v-model="formData.username"
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('username')"
                />
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'username' })"
                >
                  {{ error({ fieldName: "username", displayName: "Email" }) }}
                </div>
              </div>
              <!-- password -->
              <div
                class="fts-form-group fts-input-group with-check"
                :class="{ 'with-error': error({ fieldName: 'password' }) }"
              >
                <font-awesome-icon
                  :icon="['fas', 'lock']"
                  class="fts-form-icon"
                />
                <input
                  type="password"
                  class="fts-form-control"
                  placeholder="Password"
                  autocomplete="current-password"
                  v-model="formData.password"
                />
                <font-awesome-icon
                  :icon="['far', 'check-circle']"
                  fixed-width
                  class="fts-form-icon-check"
                  v-if="checkIndicator('password')"
                />
                <div
                  class="fts-form-error"
                  v-if="error({ fieldName: 'password' })"
                >
                  {{ error({ fieldName: "password", displayName: "Password" }) }}
                </div>
              </div>
              <!-- actions -->
              <div class="login-form-actions">
                <div class="login-form-action-text action-text-right">
                  <router-link :to="{ name: 'forgotPassword' }" custom v-slot="{ navigate }">
                    <div class="action-text-btn" @click="navigate">
                      Forgot/reset password
                    </div>
                  </router-link>
                </div>
              </div>
              <!-- action -->
              <fts-btn
                type="submit"
                class="login-form-action"
                color="var(--pcd-green)"
                text-transform-init
                box-shadow-none
              >
                Sign in
              </fts-btn>
            </form>
            <template v-if="thirdPartyLogin">
              <!-- third party login -->
              <div class="divider">
                <div class="divider-line" />
                <div class="divider-label">or</div>
                <div class="divider-line" />
              </div>
              <!-- google login -->
              <fts-btn
                class="login-form-action google-sign-in"
                color="#ffffff"
                text-transform-init
                box-shadow-none
                @click="googleLogin"
              >
                <img style="margin-right: 16px;" width="20" src="../../assets/images/brand/google.png" />
                Sign in with Google
              </fts-btn>
              <!-- apple login -->
              <vue-apple-login
                color="black"
                :border="true"
                type="continue"
                width="212"
                height="46"
                :onSuccess="onSuccess"
                :onFailure="onFailure"
                class="cust-apple-login"
              />
            </template>
            <!-- create -->
            <div class="login-form-create">
              Don't have an account?
              <router-link :to="{ name: 'createAccount' }">
                Sign up
              </router-link>
            </div>
          </div>
          <!-- build version -->
          <div class="app-build-version" v-if="buildVersion">
            Version {{buildVersion}}
          </div>
        </div>
      </div>
    </div>
    <div class="dummy-element"></div>
    <!-- spinner -->
    <fts-spinner :show="loading" full-screen tabindex="0" ref="loadingOverlay" />
    <!-- feedback indicate -->
    <fts-feedback-indicate ref="ftsFeedbackIndicate" class="login-feedback-indicate">
      <template #action="{uid, close}">
        <fts-btn color="primary" text text-transform-init @click="close" v-if="uid === 'joinError'">
          Back to sign in
        </fts-btn>
      </template>
    </fts-feedback-indicate>
    <!-- feedback indicate modal -->
    <fts-feedback-indicate modal ref="ftsFeedbackIndicateModal">
      <template #action="{uid, close}">
        <fts-btn color="primary" text text-transform-init @click="close" v-if="uid === 'resetError'">
          Back to sign in
        </fts-btn>
      </template>
    </fts-feedback-indicate>
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required, email, sameAs } from "vuelidate/lib/validators";

import { authorizationStore } from "../store/authorizationStore";
import designValidator from "../commons/designValidator";
import { passwordValidator } from "../commons/validator";
import webAppConfig from "../commons/webApp.config";

import Checkbox from "vue-material-checkbox";

import FTSSpinner from "../elements/FTSSpinner";
import FTSBtn from "../elements/FTSBtn";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";

import GAuth from "vue-google-oauth2";
const gauthOption = {
  clientId:
    "664109851190-3ttfk39akgebtpo5rhmrdflt3nehuiie.apps.googleusercontent.com",
  scope: "profile email",
  prompt: "select_account",
};
Vue.use(GAuth, gauthOption);

import VueAppleLogin from "vue-apple-login";

Vue.use(VueAppleLogin, {
  clientId: "com.gneysoftware.fetias.service",
  scope: "name email",
  redirectURI: `${window.location.origin}/api/user/AppleSignIn`,
  state: "123",
  usePopup: true,
});

export default {
  name: "login",
  metaInfo() {
    return {
      title: `${this.metaInfo.title} - Inistate`,
    };
  },
  components: {
    Checkbox,
    "fts-spinner": FTSSpinner,
    "fts-btn": FTSBtn,
    "fts-feedback-indicate": FTSFeedbackIndicate,
  },
  data: () => ({
    loading: false,
    formData: {},
    workspaceError: null,
    loaded: false,
    isError: false,
    fixedUsername: false,
    joinExistingWorkspace: false,
    /* build version */
    buildVersion: webAppConfig.buildVersion,
  }),
  validations() {
    let formData = {};
    if (this.state === "join") {
      formData = {
        displayName: { required },
        username: { required, email },
      };

      if (!this.formData.existing) {
        formData.workspace = { 
          required,
          format: designValidator.workspaceNameFormat,
        };
      }

      if (!this.formData.provider) {
        (formData.password = {
          required,
          password: passwordValidator,
        }),
          (formData.confirmPassword = {
            required,
            sameAs: sameAs(() => this.formData.password),
            password: passwordValidator,
          });
      }
    } else if (this.state === "forgotPassword") {
      formData.username = { required, email };
    } else if (this.state === "reset") {
      (formData.password = {
        required,
        password: passwordValidator,
      }),
        (formData.confirmPassword = {
          required,
          sameAs: sameAs(() => this.formData.password),
          password: passwordValidator,
        });
    } else if (this.state === "createAccount") {
      formData.email = { required, email }
    } else {
      formData.username = { required };
      formData.password = { required };
    }

    return { formData };
  },
  methods: {
    /* init */
    async init() {
      /* reset auth */
      if (
        this.state == "join" ||
        this.state == "reset" ||
        this.state == "forgotPassword"
      ) {
        localStorage.removeItem("ls.authorizationData");
      }
      /* reset */
      if (this.state == "reset") {
        this.formData.username = this.$route.query.email;
        this.formData.code = this.$route.query.code;
        if (!(this.formData.username && this.formData.code)) {
          this.isError = true;
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            uid: "joinError",
            desc: "Invalid URL link.",
            handler: () => {
              this.$router.push({ name: 'login' });
              this.isError = false;
            },
          });
        }
      }
      /* join */
      else if (this.state == "join") {
        let route = this.$route;
        try {
          let joinInfo = await authorizationStore.dispatch("joinInformation", {
            registration: route.query.id,
            code: route.query.code,
          });

          this.formData = joinInfo.data;
          this.fixedUsername = this.formData.username != null;
        } catch(err) {
          this.isError = true;
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            uid: "joinError",
            response: err.response,
            handler: () => {
              this.$router.push({ name: 'login' });
              this.isError = false;
            },
          });
        }
      } 
      /* activate */
      else if (this.state == "activate") {
        let route = this.$route;
        this.formData = {
          registration: route.query.id,
          code: route.query.code,
        };
        this.activateWorkspace();
      }
    },
    /* redirect */
    redirect() {
      if (this.$route.name == 'reset') {
        this.$router.push({ name: 'login' });
      } else if (this.$route.name == "join") {
        if (this.formData.existing) {
          // this.$router.push({ name: 'dashboard' });
          location.href="./#/dashboard";
        } else {
          // this.$router.push({ name: 'studio' });
          location.href="./#/studio";
        }
      } else {
        // this.$router.push({ name: 'dashboard' });
        location.href="./#/dashboard";
      }
    },
    async requestReset() {
      if (!this.loading) {
        this.loading = true;
        this.$v.formData.$touch();
        if (this.$v.formData.$invalid) {
          console.info("form invalid");
        } else {
          try {
            await authorizationStore.dispatch(
              "forgotPassword",
              this.formData
            );

            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "We have emailed you the instruction to reset your password.",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          } catch (err) {
            this.$toasted.global.error({
              response: err.response,
            });
          }
        }
        this.loading = false;
      }
    },
    async resetPassword() {
      if (!this.loading) {
        this.loading = true;
        this.$v.formData.$touch();
        if (this.$v.formData.$invalid) {
          console.info("form invalid");
        } else {
          try {
            await authorizationStore.dispatch("resetPassword", {
              email: this.formData.username,
              code: this.formData.code,
              password: this.formData.password,
            });

            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Your password has been reset",
              desc: "You now may continue with your new password.",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          } catch (err) {
            this.$refs.ftsFeedbackIndicateModal.show({
              type: "error",
              uid: "resetError",
              response: err.response,
              handler: () => {
                this.$router.push({ name: 'login' });
              }
            })
          }
        }
        this.loading = false;
      }
    },
    async createAccount() {
      if (!this.loading) {
        this.loading = true;
        this.$v.formData.$touch();
        if (this.$v.formData.$invalid) {
          console.info("form invalid");
        } else {
          try {
            await authorizationStore.dispatch("register", {
              companyName: this.formData.companyName,
              displayName: this.formData.displayName,
              email: this.formData.email,
              templateId: this.$route.query.templateId
            });

            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "You are almost there! We have sent an activation email. Please activate to verify your workspace.",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          } catch (err) {
            if (
              err.response &&
              err.response.data &&
              err.response.data.message
            ) {
              this.$toasted.error(err.response.data.message);
            } else {
              this.$toasted.global.error();
            }
          }
        }
        this.loading = false;
      }
    },
    async submit() {
      if (!this.loading) {
        this.loading = true;
        this.$v.formData.$touch();
        this.$nextTick(() => {
          this.$refs.loadingOverlay && this.$refs.loadingOverlay.$el && this.$refs.loadingOverlay.$el.focus();
        });

        if (this.$v.formData.$invalid) {
          this.$toasted.error("Username and Password cannot be empty");
        } else {
          try {
            await authorizationStore.dispatch("login", {
              username: this.formData.username,
              password: this.formData.password,
            });
            this.redirect();
          } catch (err) {
            if (
              err.response &&
              err.response.data &&
              err.response.data.error_description
            ) {
              if (err.response.data.error_description === 'Password incorrect') {
                this.$toasted.error(`Your password is incorrect or expired. Please click on the 'Forgot/reset password' to change your password.`);
              } else {
                this.$toasted.error(err.response.data.error_description);
              }
            } else {
              this.$toasted.global.error();
            }
          }
        }
        this.loading = false;
      }
    },
    async activateWorkspace() {
      this.$v.formData.$touch();

      if ((this.state == "join" && !this.$v.formData.$invalid) || this.state === "activate") {
        try {
          let result = await authorizationStore.dispatch(
            "activate",
            this.formData
          );
          if (result == true) {
            this.redirect();
          } else {
            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "We have sent an activation email. Please activate through email",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          }
        } catch (err) {
          this.$refs.ftsFeedbackIndicateModal.show({
            type: "error",
            uid: "resetError",
            title: "Something went wrong",
            response: err.response,
            handler: () => {
              this.$router.push({ name: 'login' });
            },
          });
        }
      }
    },
    // validator
    error(item) {
      try {
        let validator = this.$v.formData[item.fieldName];
        const displayName = item.displayName;
        if (validator.$error) {
          /* email */
          if (validator.email === false)
            return "Please enter email address in format: yourname@example.com";
          /* password */
          else if (validator.required && validator.password === false)
            return `${
              displayName ? displayName : "Password"
            } must contain 1 alphabet, 1 numeric, and at least 8 characters.`;
          /* same as */
          else if (validator.required && validator.sameAs === false)
            return `${displayName ? displayName : "Field"} must be identical.`;
          /* workspace format */
          else if (validator.format === false) 
            return `${displayName ? displayName : "Field"} cannot contain invalid characters.`;
          /* default */
          else return `${displayName ? displayName : "Field"} is required.`;
        }
      } catch (err) {}
    },
    checkIndicator(fieldName) {
      try {
        return (
          this.formData[fieldName] && !this.$v.formData[fieldName].$invalid
        );
      } catch (err) {}
    },
    onInput(fieldName) {
      const fieldValidator = this.$v.formData[fieldName];

      if (fieldValidator && fieldValidator.required === false)
        fieldValidator.$touch();
    },
    onBlur(fieldName) {
      this.$v.formData[fieldName] && this.$v.formData[fieldName].$touch();
    },
    /* google register */
    async googleRegister() {
      try {
        const authCode = await this.$gAuth.getAuthCode();
        console.log(authCode);
        const response = await authorizationStore.dispatch('externalLogin', {
          authCode: authCode,
          type: "google",
          register: true,
          templateId: this.$route.query.templateId,
          ...this.formData
        });

        if (response.data.redirect) {
          location.href = `${window.location.origin}/${response.data.redirect}`;
        }
      } catch(err) {
        if (err && err.error === 'popup_closed_by_user') {
          console.info("Google Login", err.error);
        } else {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
          });
        }
      }
    },
    /* google login */
    async googleLogin() {
      try {
        const authCode = await this.$gAuth.getAuthCode();
        console.log(authCode);
        const response = await authorizationStore.dispatch("externalLogin", {
          registrationId: this.$route.query.id,
          code: this.$route.query.code,
          authCode: authCode,
          type: "google",
          ...this.formData,
        });

        if (response === true) {
          this.redirect();
        }
      } catch (err) {
        if (err && err.error === 'popup_closed_by_user') {
          console.info("Google Login", err.error);
        } else {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
          });
        }
      }
    },
    /* apple register */
    async appleRegisterSuccess(data) {
      try {
        const response = await authorizationStore.dispatch("externalLogin", {
          authCode: data.authorization.code,
          type: "apple",
          register: true,
          templateId: this.$route.query.templateId,
          ...this.formData
        });
        
        if (response.data.redirect) {
          location.href = `${window.location.origin}/${response.data.redirect}`;
        }
      } catch(err) {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
        });
      }
    },
    /* apple login */
    async onSuccess(data) {
      try {
        this.formData.username = null;
        this.formData.password = null;
        const response = await authorizationStore.dispatch("externalLogin", {
          registrationId: this.$route.query.id,
          code: this.$route.query.code,
          authCode: data.authorization.code,
          type: "apple",
          ...this.formData,
        });

        if (response === true) {
          this.redirect();
        }
      } catch (err) {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
        });
      }
    },
    onFailure(err) {
      if (err && err.detail && err.detail.error === 'popup_closed_by_user') {
        console.info("Continue with Apple:", err.detail.error);
      } else {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
        });
      }
    },
  },
  computed: {
    companyLogo() {
      return authorizationStore.getters.appLogo;
    },
    companyName() {
      return authorizationStore.getters.appName;
    },
    thirdPartyLogin() {
      return !Boolean(authorizationStore.getters.appDisableThirdPartyLogin);
    },
    metaInfo() {
      switch(this.$route.name) {
        case 'activate':
          return {
            title: 'Activate Account'
          }
        case 'createAccount':
          return {
            title: 'Create Account'
          }
        case 'forgotPassword':
          return {
            title: 'Forgot Password'
          }
        case 'join':
          return {
            title: 'Join'
          }
        case 'reset':
          return {
            title: 'Reset Account'
          }
        default:
          return {
            title: 'Login'
          }
      }
    },
    state() {
      return this.$route.name;
    },
    workspaceNameSpecialCharacters() {
      return designValidator.workspaceNameSpecialCharacters.split("");
    },
  },
  watch: {
    state(newVal, oldVal) {
      if (newVal !== oldVal) {
        // reset
        this.$v.$reset();
        this.formData = {};
        this.isError = false;
        this.$refs.ftsFeedbackIndicate.reset();
        this.$refs.ftsFeedbackIndicateModal.reset();
        this.init();
      }
    },
  },
  async mounted() {
    await webAppConfig.getConfig();
    this.init();
    
    if (localStorage.getItem("ls.authorizationData")) {
      this.redirect();
    }
    
    this.loaded = true;
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

:root {
  --pcd-green-50: rgb(219, 233, 198);
  --pcd-green: rgb(34, 102, 52);
  --pcd-brown: rgb(193, 146, 103);
  --pcd-brown-rgb: 193, 146, 103;
}

.fts-login {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  &::after {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: var(--pcd-green-50);
    z-index: -1;
    opacity: 0.75;
  }
  .login-content {
    flex: 1;
    display: flex;
    align-items: center;
    .login-content-wrapper {
      position: relative;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: 2rem auto auto;
      border-radius: 0.5rem;
      box-shadow: var(--fts-elevation-1);
      background-color: #fff;
      .login-intro {
        flex: 1 1 532px;
        min-height: 5rem; // To prevent UI issue when img load error.
        position: relative;
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
        padding: 1rem;
        background-color: #fff;
        &:after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
        }
        .login-intro-header {
          z-index: 1;
          position: absolute;
          top: 1.5rem;
          left: 1.5rem;
          right: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          .login-intro-header-info {
            display: flex;
            align-items: center;
            .login-intro-header-logo {
              height: 2rem;
            }
            .login-intro-header-name {
              margin-left: 0.75rem;
              font-size: 2rem;
              font-weight: 500;
              color: var(--fts-primary);
            }
          }
        }
        .login-intro-content {
          max-width: 768px;
          width: 100%;
        }
      }
      .login-form {
        position: relative;
        flex: 1 1 460px;
        min-width: 460px;
        max-width: 460px;
        padding: 1.5rem 1.5rem 3rem;
        background: rgba(255, 255, 255, 0.875);
        backdrop-filter: blur(8px);
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
        overflow: hidden;
        transition: all 0.3s ease-in-out;
        .login-form-title {
          margin-bottom: 1.5rem;
          .login-form-title-main {
            font-weight: 500;
            font-size: 1.75rem;
            line-height: 1.5;
          }
          .login-form-title-sub {
            font-weight: 300;
            font-size: 0.875rem;
            line-height: 1.45;
            color: #757575;
          }
        }
        .workspace-section {
          margin-bottom: 1.5rem;
          .workspace-label {
            font-size: 0.875rem;
          }
          .workspace-name {
            margin-top: 0.375rem;
            font-weight: bold;
            font-size: 1.5rem;
          }
        }
        .login-form-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .login-form-action-text {
          margin-top: -0.375rem;
          margin-bottom: calc(1.75rem - 0.375rem);
          display: flex;
          align-items: center;
          &.action-text-left {
            flex: 1;
            justify-content: flex-start;
            .action-text-btn {
              padding: 0.375rem 0.75rem 0.375rem 0;
              &:hover {
                padding-left: 0.75rem;
              }
            }
          }
          &.action-text-right {
            flex: 1;
            justify-content: flex-end;
            .action-text-btn {
              padding: 0.375rem 0 0.375rem 0.75rem;
              &:hover {
                padding-right: 0.75rem;
              }
            }
          }
          &.action-text-center {
            justify-content: center;
            .action-text-btn {
              padding: 0.375rem 0;
              &:hover {
                padding-left: 0.75rem;
                padding-right: 0.75rem;
              }
            }
          }
          .action-text-btn {
            display: inline-block;
            padding: 0.375rem 0.75rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            color: var(--pcd-brown);
            transition: padding 0.3s ease-in-out;
            &:hover {
              background-color: rgba(var(--pcd-brown-rgb), 0.1);
              cursor: pointer;
            }
          }
        }
        .login-form-action {
          height: auto;
          width: 100%;
          padding: 0 1.5rem;
          border-radius: 0.5rem;
          box-shadow: var(--fts-elevation-2);
          font-size: 1rem;
          line-height: 2.875rem;
        }

        .login-form-action.google-sign-in {
          box-shadow: var(--fts-elevation-2);
          color: #757575;
          letter-spacing: 0;
        }

        .login-form-create {
          margin-top: 2rem;
          font-size: .875rem;
          line-height: 1.5rem;
          color: var(--fts-gray-5);
          a {
            color: var(--pcd-brown);
            &:not(:hover) {
              text-decoration: none;
            }
          }          
        }
        .login-form-term-policy {
          margin-top: 1.5rem;
          font-size: .75rem;
          line-height: 1rem;
          color: var(--fts-gray-5);
          a {
            color: var(--pcd-brown);
            &:not(:hover) {
              text-decoration: none;
            }
          } 
        }
      }
    }
  }
  /* utilities */
  .fts-form-group {
    margin-bottom: 2rem;
    &.fts-input-group {
      position: relative;
      .fts-form-icon {
        position: absolute;
        top: 0.85rem;
        left: 0.75rem;
      }
      .fts-form-control {
        padding-left: 2.5rem;
      }
    }
    &.with-error {
      .fts-form-icon {
        color: $dangerColor;
      }
      .fts-form-control {
        border-color: $dangerColor;
        box-shadow: inset 0 1px 1px rgba($dangerColor, 0.075),
          0 0 4px rgba($dangerColor, 0.5);
      }
    }
    &.with-check {
      .fts-form-icon-check {
        position: absolute;
        top: 0.85rem;
        right: 0.75rem;
      }
      .fts-form-control {
        padding-right: 2.5rem;
      }
    }
  }
  .fts-form-icon {
    color: $grayLightColor;
    pointer-events: none;
    z-index: 1;
  }
  .fts-form-icon-check {
    color: $successColor;
    pointer-events: none;
  }
  .fts-form-control {
    @include placeholder(
      $fontSize: 0.875rem,
      $fontWeight: 300,
      $color: $grayLightColor
    );
    height: calc(1.5em + 1rem + 2px) !important;
    font-size: 1rem;
    border-radius: 0.25rem;
  }
  .fts-form-helper {
    margin-top: 0.375rem;
    margin-bottom: 0;
    font-weight: 300;
  }
  .fts-form-error {
    margin-top: 0.375rem;
    font-size: 0.675rem;
    font-weight: 300;
    color: $dangerColor;
  }
  /* login feedback indicate */
  .login-feedback-indicate {
    width: 365px;
    margin: 0 auto;
    border-radius: $comfortDesignBorderRadius;
    background: #fff;
  }
  /* workspace name hint */
  .workspace-name-hint {
    margin-top: .575rem;;
    .workspace-name-hint-wrapper {
      font-size: .75rem;
      font-weight: 300;
      color: var(--fts-gray-5);
      line-height: 1.25;
      &:not(:first-child) {
        margin-top: .375rem;
      }
      .workspace-name-hint-highlight {
        margin-left: .275rem;
        font-weight: 500;
      }
    }
  }
  /* login exist checkbox */
  .login-exist-checkbox {
    display: flex;
    align-items: flex-start;
    .m-chckbox--container {
      display: block;
      width: auto;
      margin: 0;
    }
    .m-chckbox--label {
      display: none;
    }
    > label {
      margin-left: .875rem;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
    }
  }
  /* login exist helper */
  .login-exist-helper {
    margin-bottom: 3rem;
    font-size: 1rem;
    color: var(--fts-form-label);
    line-height: 1.5rem;
  }
  /* login sign up email */
  .login-sign-up-email {
    margin-bottom: .75rem;
  }
}
/* media query */
@media (max-width: 991.98px) {
  .fts-login {
    .login-content {
      .login-content-wrapper {
        margin-top: 0;
        background: none;
        box-shadow: none;
        .login-intro {
          border-radius: 0;
        }
        .login-form {
          position: absolute;
          top: 5rem;
          min-width: 0;
          width: 90%;
          max-width: 460px;
          box-shadow: var(--fts-elevation-3);
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          z-index: 1;
        }
      }
    }
  }
}
/* dummy element */
.dummy-element {
  padding: 1.7rem;
}
/* divider */
.divider {
  display: flex;
  align-items: center;
  line-height: 1;
  &.divider--mx-1 {
    .divider-line {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }
  }
  .divider-line {
    flex: 1;
    margin-top: 2rem;
    margin-bottom: 2rem;
    border-top: 1px solid var(--fts-gray-3);
  }
  .divider-label {
    padding: 0 1rem;
    color: var(--fts-gray-5);
    font-size: 0.875rem;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
}
/* custom apple login */
.cust-apple-login {
  display: flex;
  justify-content: center;
  margin-top: 0.575rem;
  border-radius: 0.5rem;
  background: black;
  cursor: pointer;
}
/* spinner */
.fts-spinner {
  &.spinner-full-screen {
    background-color: rgba(0, 0, 0, 0.25);
  }
}
</style>

<style lang="scss" scoped>
/* app build version */
.app-build-version {
  position: absolute;;
  bottom: 1rem;
  right: 1.5rem;
  color: var(--fts-gray-4);
  font-size: .75rem;
}
</style>